import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, Routes, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AccountCommunication, AccountProfile, HelpAndFeedback } from '@resy/ui-kitchen';
import { selectIsConcierge } from '../../../../../ui-kitchen/src/reducers/userSlice';
import AccountPageWrapper from './AccountPageWrapper';
import {
  AccountPaymentMethodsOutlet,
  AccountReservationsOutlet,
  FavoritesContainerOutlet,
  MultipleListsContainerOutlet,
  MultipleListsDetailContainerOutlet,
} from './AccountPageOutlets';

// Replaces the `isConcierge` resolve that the standalone
// `/account/concierge-reservations` AngularJS route used to do, now that
// the section is served by the consolidated `/account/:section/...` route.
// Renders nothing until user data has loaded so we don't flash a redirect.
const ConciergeOnly = ({ children }) => {
  const userData = useSelector((state) => state.user?.userData);
  const isConcierge = useSelector(selectIsConcierge);
  if (userData == null) return null;
  if (!isConcierge) return <Navigate to="/account/profile" replace />;
  return children;
};
ConciergeOnly.propTypes = { children: PropTypes.node.isRequired };

const AccountPageRoutes = (props) => {
  const {
    $location, $rootScope, $route, $routeParams, Analytics, AutomationMode,
  } = props;
  return (
    <Routes>
      <Route element={<AccountPageWrapper {...props} />}>
        <Route
          path="profile"
          element={(
            <AccountProfile
              $rootScope={$rootScope}
              Analytics={Analytics}
              context="resy.com"
            />
          )}
        />
        <Route
          path="favorites"
          element={(
            <FavoritesContainerOutlet
              $rootScope={$rootScope}
              Analytics={Analytics}
            />
          )}
        />
        <Route
          path="lists"
          element={(
            <MultipleListsContainerOutlet
              $location={$location}
              $rootScope={$rootScope}
              $routeParams={$routeParams}
              Analytics={Analytics}
            />
          )}
        />
        <Route
          path="lists/:listId"
          element={(
            <MultipleListsDetailContainerOutlet
              $rootScope={$rootScope}
              $routeParams={$routeParams}
              Analytics={Analytics}
            />
          )}
        />
        <Route
          path="lists/:urlSlug/view"
          element={(
            <MultipleListsDetailContainerOutlet
              $rootScope={$rootScope}
              $routeParams={$routeParams}
              Analytics={Analytics}
            />
          )}
        />
        <Route
          path="payment-methods/:modal?"
          element={(
            <AccountPaymentMethodsOutlet
              Analytics={Analytics}
              AutomationMode={AutomationMode}
            />
          )}
        />
        <Route
          path="sca/payment-methods/:modal?"
          element={(
            <AccountPaymentMethodsOutlet
              Analytics={Analytics}
              AutomationMode={AutomationMode}
            />
          )}
        />
        <Route
          path="communication"
          element={(
            <AccountCommunication
              Analytics={Analytics}
              context="resy.com"
            />
          )}
        />
        <Route
          path="reservations-and-notify"
          element={(
            <AccountReservationsOutlet
              $location={$location}
              $rootScope={$rootScope}
              $route={$route}
              Analytics={Analytics}
            />
          )}
        />
        <Route
          path="concierge-reservations"
          element={(
            <ConciergeOnly>
              <AccountReservationsOutlet
                $location={$location}
                $rootScope={$rootScope}
                $route={$route}
                Analytics={Analytics}
                bookOnBehalfOf
              />
            </ConciergeOnly>
          )}
        />
        <Route path="help-and-feedback" element={<HelpAndFeedback context="resy.com" />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Route>
    </Routes>
  );
};

AccountPageRoutes.propTypes = {
  $location: PropTypes.shape({}).isRequired,
  $rootScope: PropTypes.shape({}).isRequired,
  $route: PropTypes.shape({}).isRequired,
  $translate: PropTypes.func.isRequired,
  $routeParams: PropTypes.shape({}).isRequired,
  Analytics: PropTypes.shape({}).isRequired,
  AutomationMode: PropTypes.bool.isRequired,
};

export default AccountPageRoutes;
